<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="loadAsyncData">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.enabled') }}</option>
              <option value="false">{{ $t('attributes.disabled') }}</option>
            </b-select>
          </b-field>
          <b-field>
            <b-autocomplete
              v-model="collaborate_name"
              name="collaborate"
              field="name"
              :data="filteredCollaborates"
              :loading="isLoadingCollaborates"
              :placeholder="
                $t('attributes.collaborate') + $t('attributes.name')
              "
              :open-on-focus="true"
              @typing="filteringCollaborates"
              @select="onSelectCollaborate"
            >
              <template #empty>
                No results found
              </template>
            </b-autocomplete>
          </b-field>
          <b-field>
            <b-input
              v-model="search"
              placeholder="Search..."
              type="search"
              icon="magnify"
              @input="onSearching"
            ></b-input>
            <p class="control">
              <button class="button is-primary" @click="loadAsyncData">
                {{ $t('attributes.search') }}
              </button>
            </p>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.collaborate_activity') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :selected.sync="selectedItem"
      :loading="isLoading"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      paginated
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="active"
        :label="$t('attributes.activation_status')"
        width="95"
        sortable
        style="vertical-align: middle"
        v-slot="props"
      >
        <b-icon :icon="props.row.active ? 'check' : 'close'"></b-icon>
      </b-table-column>

      <b-table-column
        field="id"
        :label="$t('attributes.id')"
        width="95"
        numeric
        sortable
        style="vertical-align: middle"
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="collaborate_name"
        :label="$t('attributes.collaborate')"
        width="200"
        sortable
        v-slot="props"
      >
        {{ `[${props.row.collaborate_id}] ${props.row.collaborate_name}` }}
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        style="min-width: 200px"
        sortable
        v-slot="props"
      >
        {{ props.row.name }}
      </b-table-column>

      <b-table-column
        label="報名網址"
        style="min-width: 200px"
        centered
        v-slot="props"
      >
        <a
          :href="collaborateUrl(props.row.collaborate_id)"
          target="_blank"
          class="underline"
        >
          連結
        </a>
        <b-button
          type="is-ghost"
          icon-left="content-copy"
          style="color: #9e6305; font-size: 0.5rem"
          @click="onCopyCollaborateUrl(props.row.collaborate_id)"
        ></b-button>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
            <p>{{ $t('attributes.empty') }}</p>
          </div>
        </section>
      </template>
    </b-table>

    <section v-if="selectedItem">
      <b-tabs
        v-model="activeTab"
        :animated="false"
        type="is-boxed"
        class="boarder-tabs"
        destroyOnHide
        expanded
      >
        <b-tab-item label="基本資料" :disabled="isLoading">
          <activity-basic-form
            v-bind="selectedItem"
            :sending="sending"
            @onSend="onModify"
          />
        </b-tab-item>
        <b-tab-item label="通知信設定" :disabled="isLoading">
          <activity-notify-form
            v-bind="selectedItem"
            :sending="sending"
            @onSend="onModifyNotify"
          />
        </b-tab-item>
        <b-tab-item label="補助設定" :disabled="isLoading">
          <activity-subsidy-form :id="selectedItem.id" />
        </b-tab-item>
        <b-tab-item label="眷屬補助" :disabled="isLoading">
          <activity-dependent-form :id="selectedItem.id" />
        </b-tab-item>
        <b-tab-item label="會員設定" :disabled="isLoading">
          <activity-users :activity="selectedItem" />
        </b-tab-item>
      </b-tabs>
    </section>

    <b-modal
      :active.sync="isModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <activity-create-form :sending="sending" @onSend="onCreate" />
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import notifyService from '@/services/notifyService'
import {
  FIRM_COLLABORATES_API,
  FIRM_COLLABORATES_ACTIVITIES_API
} from '@/utils/apis'
import ActivityCreateForm from '@/components/firm/ActivityCreateForm'
import ActivityBasicForm from '@/components/firm/ActivityBasicForm'
import ActivityNotifyForm from '@/components/firm/ActivityNotifyForm'
import ActivitySubsidyForm from '@/components/firm/ActivitySubsidyForm'
import ActivityUsers from '@/components/firm/ActivityUsers'
import ActivityDependentForm from '@/components/firm/ActivityDependentForm'

export default {
  name: 'CollaborateActivities',
  components: {
    ActivityCreateForm,
    ActivityBasicForm,
    ActivityNotifyForm,
    ActivitySubsidyForm,
    ActivityDependentForm,
    ActivityUsers
  },

  data() {
    return {
      isLoading: false,
      items: [],
      total: 0,
      filterStatus: '',
      search: '',
      sortField: 'id',
      sortOrder: 'desc',
      defaultSortOrder: 'desc',
      page: 1,
      perPage: 10,

      activeTab: 0,
      selectedItem: null,
      isModalActive: false,
      sending: false,

      isLoadingCollaborates: false,
      collaborate_name: '',
      collaborates: [],
      filteredCollaborates: [],
      selectedCollaborate: null
    }
  },
  mounted() {
    this.loadCollaborates()
  },
  methods: {
    async loadAsyncData() {
      const api = FIRM_COLLABORATES_ACTIVITIES_API

      const params = {
        collaborate_id: this.selectedCollaborate && this.selectedCollaborate.id,
        active: this.filterStatus && this.filterStatus === 'true',
        search: this.search,
        sort_by: this.sortField,
        by_descending: this.sortOrder !== 'asc',
        page_size: this.perPage,
        page_number: this.page
      }

      this.isLoading = true
      try {
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('collaborate_activity', e)
      } finally {
        this.isLoading = false
      }
    },
    async loadCollaborates() {
      const api = `${FIRM_COLLABORATES_API}/list`

      this.isLoading = true
      try {
        const res = await this.axios.get(api)
        this.collaborates = res.data.data
        this.filteringCollaborates()

        await this.loadAsyncData()
      } catch (e) {
        this.collaborates = []
        this.filteredCollaborates = []
        this.selectedCollaborate = null

        notifyService.fetch_error('collaborate', e)
      } finally {
        this.isLoading = false
      }
    },
    filteringCollaborates: debounce(function() {
      this.filteredCollaborates = this.collaborates.filter(option => {
        return (
          option.name
            .toString()
            .toLowerCase()
            .indexOf(this.collaborate_name.toLowerCase()) >= 0
        )
      })
    }, 500),
    onSelectCollaborate(option) {
      this.selectedCollaborate = option
      this.loadAsyncData()
    },

    onSearching: debounce(function() {
      this.loadAsyncData()
    }, 500),
    onPageChange(page) {
      this.page = page
      this.loadAsyncData()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadAsyncData()
    },

    onAdd() {
      this.isModalActive = true
    },
    async onCreate(item) {
      const api = `${FIRM_COLLABORATES_ACTIVITIES_API}`

      this.sending = true
      try {
        const res = await this.axios.post(api, item)
        const _item = res.data.data
        this.items = [...this.items, _item]
        this.isModalActive = false

        notifyService.success(this.$t('attributes.add_succeed'))
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async onModify(item) {
      const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/${item.id}`

      this.sending = true
      try {
        const res = await this.axios.put(api, item)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.id === _item.id)
        const items = [...this.items]
        items[idx] = _item

        this.items = items
        this.selectedItem = items.find(c => c.id === this.selectedItem.id)

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async onModifyNotify(item) {
      const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/${item.id}/notify-mail`

      this.sending = true
      try {
        const res = await this.axios.put(api, item)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.id === _item.id)
        const items = [...this.items]
        items[idx] = _item

        this.items = items
        this.selectedItem = items.find(c => c.id === this.selectedItem.id)

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    collaborateUrl(id) {
      return `${process.env.VUE_APP_COLLABORATE_URL}/login?c=${id}`
    },
    async onCopyCollaborateUrl(id) {
      const url = this.collaborateUrl(id)

      await navigator.clipboard.writeText(url)

      return notifyService.success('連結已複製到剪貼簿。')
    }
  }
}
</script>
